import React from 'react';
import { StateMetadata, CountyOption } from 'interfaces/StateMetadata';
import { SearchResult } from 'interfaces/SearchTypes';
import { getStateSupportInfo } from 'utils/stateSupportUtils';
import ReactGA from 'react-ga4';

interface StateListProps {
  stateMetadata: { [stateName: string]: StateMetadata };
  counties: CountyOption[];
  onStateSelect: (stateName: string) => void;
  searchResults?: SearchResult[];
}

const StateList = ({
  stateMetadata,
  counties,
  onStateSelect,
}: StateListProps) => {
  return (
    <div className="state-list">
      {/* Screen reader instructions */}
      <div className="sr-only" aria-live="polite">
        Available states for ballot tracking. Use Tab and Enter to navigate.
      </div>

      <div
        className="state-list-container"
        role="region"
        aria-labelledby="state-list-heading"
      >
        <h3 id="state-list-heading" className="state-list-heading">
          Available States
        </h3>

        <div className="state-list-grid" role="list">
          {Object.entries(stateMetadata)
            .sort(([a, metadataA], [b, metadataB]) =>
              metadataA.state.localeCompare(metadataB.state),
            )
            .map(([stateKey, metadata]) => {
              const properName = metadata.state;
              const supportInfo = getStateSupportInfo(metadata, counties);

              // For states with state-wide support and a URL, render a clickable link styled as a card
              if (!metadata.link_counties && metadata.url) {
                return (
                  <a
                    key={stateKey}
                    href={metadata.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      ReactGA.event({
                        category: 'hyperlink',
                        action: 'click - state list link',
                        label: metadata.url!,
                      });
                    }}
                    role="listitem"
                    aria-label={`${properName}, ${supportInfo.displayText} available - Visit BallotTrax (opens in new tab)`}
                    className={`state-list-item ${supportInfo.level} state-list-item-link`}
                    data-support-level={supportInfo.level}
                  >
                    <div className="state-list-content">
                      <div className="state-header">
                        <div className="state-name">{properName}</div>
                        <div className="state-list-visual-button">Visit ↗</div>
                      </div>
                      <div className="state-support">
                        <span className={`support-badge ${supportInfo.level}`}>
                          {supportInfo.displayText}
                        </span>
                      </div>
                    </div>
                  </a>
                );
              }

              return (
                <button
                  key={stateKey}
                  role="listitem"
                  onClick={() => {
                    if (supportInfo.isClickable) {
                      onStateSelect(stateKey);

                      // Auto-scroll to results after a brief delay to let the UI update
                      setTimeout(() => {
                        const resultsElement = document.querySelector(
                          '[data-results-section]',
                        );
                        if (resultsElement) {
                          resultsElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                          });
                        }
                      }, 300);
                    }
                  }}
                  disabled={!supportInfo.isClickable}
                  aria-label={`${properName}, ${supportInfo.displayText} available`}
                  className={`state-list-item ${supportInfo.level}`}
                  data-support-level={supportInfo.level}
                >
                  <div className="state-list-content">
                    <div className="state-header">
                      <div className="state-name">{properName}</div>
                    </div>
                    <div className="state-support">
                      <span className={`support-badge ${supportInfo.level}`}>
                        {supportInfo.displayText}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default StateList;
